import { useState } from 'react';
import { useAppStore } from '../stores/useAppStore';
import { getDefault, getTemplate } from '../templates/registry';
import { summarizeToolUse } from '../pipeline/chatTools';
import type { PromptTemplateId } from '../types';

export type ApprovalCardProps = {
  universeId: string;
  toolUseId: string;
  name: string;
  input: unknown;
  resolution?: boolean;
  onResolve: (approved: boolean) => void;
};

function CollapsibleCurrent({
  label,
  text,
}: {
  label: string;
  text: string;
}) {
  const [open, setOpen] = useState(false);
  if (!text) return null;
  return (
    <div className="approval-current">
      <button
        type="button"
        className="approval-current-toggle"
        onClick={() => setOpen((o) => !o)}
      >
        {open ? '▼' : '▶'} {label}
      </button>
      {open && <pre className="approval-text">{text}</pre>}
    </div>
  );
}

function ProposedText({ text }: { text: string }) {
  return <pre className="approval-text approval-text-new">{text}</pre>;
}

function FieldDelta({
  label,
  oldVal,
  newVal,
}: {
  label: string;
  oldVal: string | undefined;
  newVal: string;
}) {
  return (
    <div className="approval-field">
      <div className="approval-field-label">{label}</div>
      {oldVal && oldVal !== newVal && (
        <div className="approval-field-old">{oldVal}</div>
      )}
      <div className="approval-field-new">{newVal}</div>
    </div>
  );
}

function PromptDescription({ templateId }: { templateId: PromptTemplateId }) {
  return <span className="approval-templateId">{templateId}</span>;
}

export default function ApprovalCard({
  universeId,
  toolUseId: _toolUseId,
  name,
  input,
  resolution,
  onResolve,
}: ApprovalCardProps) {
  const universe = useAppStore((s) => s.universes[universeId]);
  if (!universe) return null;

  const args = (input ?? {}) as Record<string, unknown>;
  const summary = summarizeToolUse(name, input);

  const body = (() => {
    switch (name) {
      case 'update_canon': {
        const newText = String(args.newText ?? '');
        const current = universe.canon ?? '';
        return (
          <>
            <div className="approval-headline">Replace canon</div>
            <ProposedText text={newText} />
            <CollapsibleCurrent label="Current canon" text={current} />
          </>
        );
      }
      case 'set_prompt_override': {
        const tid = args.templateId as PromptTemplateId;
        const newText = String(args.newText ?? '');
        const currentOverride = universe.overrides[tid];
        const shippedDefault = getDefault(tid);
        return (
          <>
            <div className="approval-headline">
              Override prompt <PromptDescription templateId={tid} />
            </div>
            <ProposedText text={newText} />
            {currentOverride != null && (
              <CollapsibleCurrent
                label="Current override"
                text={currentOverride}
              />
            )}
            <CollapsibleCurrent label="Shipped default" text={shippedDefault} />
          </>
        );
      }
      case 'clear_prompt_override': {
        const tid = args.templateId as PromptTemplateId;
        const currentOverride = universe.overrides[tid];
        return (
          <>
            <div className="approval-headline">
              Revert <PromptDescription templateId={tid} /> to shipped default
            </div>
            <CollapsibleCurrent
              label="Current override (will be removed)"
              text={currentOverride ?? ''}
            />
          </>
        );
      }
      case 'set_prompt_extension': {
        const tid = args.templateId as PromptTemplateId;
        const newText = String(args.newText ?? '');
        const currentExt = universe.extensions[tid];
        return (
          <>
            <div className="approval-headline">
              Set extension for <PromptDescription templateId={tid} />
            </div>
            <ProposedText text={newText} />
            {currentExt && (
              <CollapsibleCurrent
                label="Current extension"
                text={currentExt}
              />
            )}
            <CollapsibleCurrent
              label="Effective template (without extension)"
              text={getTemplate(tid, universeId).replace(
                currentExt ? `\n\n${currentExt}` : '',
                ''
              )}
            />
          </>
        );
      }
      case 'clear_prompt_extension': {
        const tid = args.templateId as PromptTemplateId;
        const currentExt = universe.extensions[tid];
        return (
          <>
            <div className="approval-headline">
              Clear extension for <PromptDescription templateId={tid} />
            </div>
            <CollapsibleCurrent
              label="Current extension (will be removed)"
              text={currentExt ?? ''}
            />
          </>
        );
      }
      case 'update_universe_meta': {
        return (
          <>
            <div className="approval-headline">Update universe identity</div>
            {typeof args.name === 'string' && (
              <FieldDelta
                label="Name"
                oldVal={universe.name}
                newVal={args.name}
              />
            )}
            {typeof args.description === 'string' && (
              <FieldDelta
                label="Description"
                oldVal={universe.description}
                newVal={args.description}
              />
            )}
            {typeof args.defaultProtagonists === 'string' && (
              <FieldDelta
                label="Default protagonists"
                oldVal={universe.defaultProtagonists}
                newVal={args.defaultProtagonists}
              />
            )}
          </>
        );
      }
      default:
        return (
          <>
            <div className="approval-headline">{summary}</div>
            <pre className="approval-text">{JSON.stringify(args, null, 2)}</pre>
          </>
        );
    }
  })();

  const stateClass =
    resolution === true
      ? 'applied'
      : resolution === false
        ? 'rejected'
        : 'pending';

  return (
    <div className={`approval-card approval-${stateClass}`}>
      <div className="approval-status-row">
        <span className="approval-status-label">
          {resolution === true && '✓ Applied'}
          {resolution === false && '✗ Rejected'}
          {resolution === undefined && 'Needs review'}
        </span>
        <span className="approval-summary dim small">{summary}</span>
      </div>
      {body}
      <div className="approval-actions">
        <button
          className={`primary ${resolution === true ? 'committed' : ''}`}
          onClick={() => onResolve(true)}
          disabled={resolution !== undefined}
        >
          Apply
        </button>
        <button
          className={`ghost ${resolution === false ? 'committed' : ''}`}
          onClick={() => onResolve(false)}
          disabled={resolution !== undefined}
        >
          Reject
        </button>
      </div>
    </div>
  );
}
